import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { WifiSlash, CloudArrowUp, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import * as offlineQueue from "../lib/offlineQueue";
import offlinePhotoQueue from "../lib/offlinePhotoQueue";

/**
 * Floating indicator + dropdown that shows pending offline ops and connection state.
 * Renders:
 *   - A small pill in the bottom-right when the app is offline OR has queued ops.
 *   - Click to expand a list of queued actions with a manual "Sync now" button.
 *   - Auto-dismisses once the queue is empty AND the app is back online for >4s.
 *
 * Mounts at document.body via a portal so it overlays everything.
 */
export default function OfflineQueueIndicator() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [recentlyReconnected, setRecentlyReconnected] = useState(false);

  useEffect(() => {
    const up = () => {
      setOnline(true);
      setRecentlyReconnected(true);
      // Auto-flush fires in offlineQueue too; we just refresh the list after a beat
      setTimeout(() => setItems(offlineQueue.list()), 800);
      setTimeout(() => setRecentlyReconnected(false), 4500);
    };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  useEffect(() => {
    const unsub = offlineQueue.subscribe((next) => setItems(next || []));
    return unsub;
  }, []);

  // Photos / completions waiting in the media queue (IndexedDB)
  const [media, setMedia] = useState({ photos: 0, completions: 0 });
  useEffect(() => {
    const unsub = offlinePhotoQueue.subscribe((c) => setMedia(c || { photos: 0, completions: 0 }));
    return unsub;
  }, []);
  const mediaCount = (media.photos || 0) + (media.completions || 0);

  // Toast on successful drain
  const prevCount = items.length;
  useEffect(() => {
    if (online && prevCount > 0 && items.length === 0) {
      toast.success("Offline actions synced");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, online]);

  const manualSync = async () => {
    if (flushing || !online) return;
    setFlushing(true);
    try {
      const { ok, failed, attempted } = await offlineQueue.flush();
      const mediaRes = await offlinePhotoQueue.flush().catch(() => ({ uploaded: 0, completed: 0, failed: 0 }));
      const totalAttempted = attempted + (mediaRes.uploaded || 0) + (mediaRes.completed || 0) + (mediaRes.failed || 0);
      if (totalAttempted === 0) {
        toast.info("Nothing to sync");
      } else if (failed > 0) {
        toast.warning(`Synced ${ok} of ${attempted} — ${failed} failed`);
      } else if (ok > 0) {
        toast.success(`Synced ${ok} action${ok === 1 ? "" : "s"}`);
      }
    } finally {
      setFlushing(false);
    }
  };

  const visible = !online || items.length > 0 || mediaCount > 0 || recentlyReconnected;
  if (!visible || typeof document === "undefined") return null;

  const totalQueued = items.length + mediaCount;
  const pillLabel = !online
    ? `Offline · ${totalQueued} queued`
    : totalQueued > 0
    ? `${totalQueued} queued`
    : "Synced";

  const pillClass = !online
    ? "bg-amber-500/90 text-white"
    : totalQueued > 0
    ? "bg-sky-600 text-white"
    : "bg-emerald-600 text-white";

  return createPortal(
    <div className="fixed bottom-4 md:bottom-4 right-4 z-[9999] flex flex-col items-end gap-2 pointer-events-none [@media(max-width:767px)]:bottom-[calc(7rem+env(safe-area-inset-bottom))]">
      {open && (items.length > 0 || mediaCount > 0) && (
        <div
          className="pointer-events-auto w-80 max-h-80 overflow-y-auto rounded-xl shadow-2xl bg-background/95 backdrop-blur border border-border"
          data-testid="offline-queue-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div>
              <p className="text-sm font-semibold">Pending sync</p>
              <p className="text-[11px] text-muted-foreground">
                {online ? "Tap to retry now" : "Will send when you're back online"}
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-foreground p-1"
              data-testid="offline-queue-close"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
          <ul className="divide-y divide-border">
            {media.photos > 0 && (
              <li className="px-4 py-2.5 text-xs" data-testid="queue-item-photos">
                <p className="font-medium">{media.photos} photo{media.photos > 1 ? "s" : ""} waiting to upload</p>
                <p className="text-[10px] text-muted-foreground">Saved on this device — uploads automatically</p>
              </li>
            )}
            {media.completions > 0 && (
              <li className="px-4 py-2.5 text-xs" data-testid="queue-item-completions">
                <p className="font-medium">{media.completions} job completion{media.completions > 1 ? "s" : ""} pending</p>
                <p className="text-[10px] text-muted-foreground">Submits after queued photos finish uploading</p>
              </li>
            )}
            {items.map((it) => (
              <li key={it.id} className="px-4 py-2.5 text-xs" data-testid={`queue-item-${it.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{it.label}</p>
                    <p className="text-[10px] text-muted-foreground truncate font-mono">{it.url}</p>
                  </div>
                  {it.attempts > 0 && (
                    <span className="text-[10px] text-amber-500 shrink-0">retry {it.attempts}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="px-4 py-2 border-t border-border flex justify-between items-center">
            <button
              onClick={() => { offlineQueue.clear(); setOpen(false); }}
              className="text-[11px] text-muted-foreground hover:text-destructive"
              data-testid="offline-queue-clear"
            >
              Clear all
            </button>
            <button
              onClick={manualSync}
              disabled={flushing || !online}
              className="text-[11px] bg-primary text-primary-foreground px-2.5 py-1 rounded disabled:opacity-50"
              data-testid="offline-queue-sync"
            >
              {flushing ? "Syncing…" : "Sync now"}
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          if (items.length > 0 || mediaCount > 0) setOpen((o) => !o);
          else if (!online) toast.warning("You're offline — actions will queue until you reconnect");
        }}
        className={`pointer-events-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-full shadow-lg text-xs font-semibold transition transform hover:scale-105 ${pillClass}`}
        data-testid="offline-queue-indicator"
        title={pillLabel}
      >
        {!online && <WifiSlash size={14} weight="fill" />}
        {online && totalQueued > 0 && <CloudArrowUp size={14} weight="fill" />}
        {online && totalQueued === 0 && <CheckCircle size={14} weight="fill" />}
        {pillLabel}
      </button>
    </div>,
    document.body,
  );
}
